import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class JwtService {
  /**
   * Decodes a JWT token
   * @param token - The JWT token to decode
   * @returns The decoded payload or null if token is invalid
   */
  public decodeToken(token: string): any {
    if (!token || token === '') {
      return null;
    }

    try {
      // Split the token into parts
      const parts = token.split('.');
      
      if (parts.length !== 3) {
        throw new Error('JWT must have 3 parts');
      }

      // Get the payload (second part)
      let payload = parts[1];
      
      // Replace URL-friendly characters with base64
      payload = payload.replace(/-/g, '+').replace(/_/g, '/');
      
      // Decode the base64 string
      const decoded = atob(payload);
      
      // Parse the JSON
      return JSON.parse(decoded);
    } catch (error) {
      // If decoding fails, return null
      return null;
    }
  }

  /**
   * Gets the expiration date from a JWT token
   * @param token - The JWT token
   * @returns The expiration date or null if not found
   */
  public getTokenExpirationDate(token: string): Date | null {
    const decodedToken = this.decodeToken(token);

    if (!decodedToken || !decodedToken.exp) {
      return null;
    }

    // Convert the expiration timestamp (seconds) to milliseconds
    const date = new Date(0);
    date.setUTCSeconds(decodedToken.exp);
    return date;
  }

  /**
   * Checks if a JWT token is expired
   * @param token - The JWT token to check
   * @returns True if token is expired, false otherwise
   */
  public isTokenExpired(token: string): boolean {
    if (!token || token === '') {
      return true;
    }

    const expirationDate = this.getTokenExpirationDate(token);
    if (!expirationDate) {
      return true;
    }

    // Check if the token has expired by comparing with current time
    return expirationDate < new Date();
  }
}